import React, { useState, useRef, useEffect } from "react";
import { ExerciseContainer } from "../../libs/exercises/types";
import { DEFAULT_CONTAINER_COLOR } from "../ProgressionsTable/ColorPicker";
import { EXERCISE_DRAG_TYPE } from "./ExerciseCard";

type Props = {
  container: ExerciseContainer;
  subCount: number;
  exerciseCount: number;
  previewNames: string[];
  onClick: () => void;
  onRename: () => void;
  onDelete: () => void;
  onDropExercise?: (exerciseId: string, targetContainerId: number | null) => void;
};

export const ExerciseContainerCard: React.FC<Props> = ({
  container,
  subCount,
  exerciseCount,
  previewNames,
  onClick,
  onRename,
  onDelete,
  onDropExercise,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const color = container.color || DEFAULT_CONTAINER_COLOR;

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(EXERCISE_DRAG_TYPE)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setIsDragOver(true);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const data = e.dataTransfer.getData(EXERCISE_DRAG_TYPE);
    if (data && onDropExercise) {
      try {
        const dragged = JSON.parse(data) as {
          id: string;
          idContainer: number | null;
        };
        if (dragged.idContainer !== container.id) {
          onDropExercise(dragged.id, container.id);
        }
      } catch {}
    }
  };

  return (
    <div
      className="relative bg-white dark:bg-gray-800 border-2 rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer p-5 flex flex-col gap-3"
      style={{
        borderColor: isDragOver ? color : `${color}20`,
        backgroundColor: isDragOver ? `${color}10` : undefined,
        transform: isDragOver ? "scale(1.02)" : undefined,
      }}
      onClick={onClick}
      onDragOver={handleDragOver}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${color}18` }}
          >
            <svg className="w-5 h-5" style={{ color }} fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              {container.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {subCount > 0 && `${subCount} sotto-cartell${subCount !== 1 ? "e" : "a"} · `}
              {exerciseCount} eserciz{exerciseCount !== 1 ? "i" : "io"}
            </p>
          </div>
        </div>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 4 20">
              <circle cx="2" cy="2" r="2" />
              <circle cx="2" cy="10" r="2" />
              <circle cx="2" cy="18" r="2" />
            </svg>
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-8 z-30 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-100 dark:border-gray-600 min-w-[150px] py-1"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => { setMenuOpen(false); onRename(); }}
              >
                Rinomina
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => { setMenuOpen(false); onDelete(); }}
              >
                Elimina
              </button>
            </div>
          )}
        </div>
      </div>

      {isDragOver ? (
        <div className="text-center text-xs font-medium py-1" style={{ color }}>
          Rilascia qui per spostare
        </div>
      ) : (
        <div className="flex flex-col gap-1 mt-1">
          {previewNames.slice(0, 3).map((name, i) => (
            <span key={i} className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {name}
            </span>
          ))}
          {exerciseCount > 3 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              +{exerciseCount - 3} altri...
            </span>
          )}
        </div>
      )}
    </div>
  );
};
